/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import handleBlurToScrollableAccordion from "@monsma/XDL/Utilities/handleBlurToScrollableAccordion";
import React, {FC, useEffect, useState} from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface OwnProps {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

// xng-change: Grab the custom settings
const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>; // xng-change

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    customSettings, // xng-change
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const showMoreLimit1 = (customSettings as any)?.attributeFacetsShowMoreLimit; // xng-change
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit1 || showMoreLimit); // xng-change
    const anySelected = facets.find(f => f.selected) !== undefined;

    useEffect(() => {
        handleBlurToScrollableAccordion();
    }, [expanded]);

    // xng-change: add onTogglePanel handler
    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
            onTogglePanel={(expanded: boolean) => {
                handleBlurToScrollableAccordion();
            }}
        >
            {limitedFacets?.map(facet => (
                <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                    >
                        {facet.name} {facet.count !== -1 && `(${facet.count})`}
                    </Checkbox>
                </CheckboxGroup>
            ))}
            {facets &&
                facets.length > (showMoreLimit1 || showMoreLimit) &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>
    );
};

export default connect(mapStateToProps)(ProductListFiltersAccordionSection);
